import { bopApi } from '@/lib/api/client';
import { truncateZeros } from '@/utils/format';

const PATH = 'v1/system/getBalances';

const QUANTITY_DECIMALS = 8;
const EURO_DECIMALS = 2;
const PERCENT = 100;

/** One row per asset (per account+asset for futures) */
export interface RawAssetRow {
  asset: string;
  /** Decimal string, toFixed(8) */
  quantity: string;
  /** Decimal string, toFixed(2) — '0.00' when unpriced */
  inEuro: string;
  lastUpdated: string;
}

export interface PlatformBalancesResponse {
  exchange: RawAssetRow[];
  instant: RawAssetRow[];
  /** One row per (account, asset) — the same asset repeats across accounts */
  futures: RawAssetRow[];
  totals: { instant: string; exchange: string; futures: string };
}

export interface ProcessedAssetRow {
  asset: string;
  /** Raw euro value — format at render with formatEuros */
  euroRaw: number;
  /** Trailing zeros stripped */
  quantity: string;
  /** Share of the section total, 0–100 */
  percentage: number;
  rank: number;
  lastUpdated: string;
}

export interface PlatformBalances {
  combined: ProcessedAssetRow[];
  exchange: ProcessedAssetRow[];
  instant: ProcessedAssetRow[];
  futures: ProcessedAssetRow[];
  /** Raw numbers — format at render with formatEuros */
  totals: { combined: number; exchange: number; instant: number; futures: number };
}

// Futures arrives as one row per (account, asset); fold to one row per asset.
// The first occurrence keeps its lastUpdated; later rows only add quantity/inEuro.
const collapseFuturesByAsset = (rows: RawAssetRow[]): RawAssetRow[] => {
  const byAsset = rows.reduce<Map<string, RawAssetRow>>((map, row) => {
    const existing = map.get(row.asset);
    if (!existing) {
      map.set(row.asset, { ...row });
      return map;
    }
    map.set(row.asset, {
      ...existing,
      quantity: (Number.parseFloat(existing.quantity) + Number.parseFloat(row.quantity)).toFixed(QUANTITY_DECIMALS),
      inEuro: (Number.parseFloat(existing.inEuro) + Number.parseFloat(row.inEuro)).toFixed(EURO_DECIMALS),
    });
    return map;
  }, new Map());
  return [...byAsset.values()];
};

const processAssets = (rows: RawAssetRow[], totalEuro: number): ProcessedAssetRow[] =>
  [...rows]
    .sort((a, b) => Number.parseFloat(b.inEuro) - Number.parseFloat(a.inEuro))
    .map((row, index) => {
      const euroRaw = Number.parseFloat(row.inEuro);
      return {
        asset: row.asset,
        euroRaw,
        quantity: truncateZeros(row.quantity),
        // Deliberate fix: the legacy page divided by a zero total and rendered 'NaN%'
        percentage: totalEuro === 0 ? 0 : (euroRaw / totalEuro) * PERCENT,
        rank: index + 1,
        lastUpdated: row.lastUpdated,
      };
    });

// Merge instant + exchange by asset, then re-sort and re-rank on the summed euro value.
const mergeCombined = (instant: ProcessedAssetRow[], exchange: ProcessedAssetRow[], totalEuro: number): ProcessedAssetRow[] => {
  const byAsset = [...instant, ...exchange].reduce<Map<string, ProcessedAssetRow>>((map, row) => {
    const existing = map.get(row.asset);
    if (!existing) {
      map.set(row.asset, { ...row });
      return map;
    }
    map.set(row.asset, {
      ...existing,
      euroRaw: existing.euroRaw + row.euroRaw,
      quantity: truncateZeros((Number.parseFloat(existing.quantity) + Number.parseFloat(row.quantity)).toFixed(QUANTITY_DECIMALS)),
    });
    return map;
  }, new Map());
  return [...byAsset.values()]
    .sort((a, b) => b.euroRaw - a.euroRaw)
    .map((row, index) => ({
      ...row,
      rank: index + 1,
      percentage: totalEuro === 0 ? 0 : (row.euroRaw / totalEuro) * PERCENT,
    }));
};

// Port of the legacy server-side reshaping (bop-ui server/api/system/getBalances).
export const mapPlatformBalances = (raw: PlatformBalancesResponse): PlatformBalances => {
  const instantTotal = Number.parseFloat(raw.totals.instant);
  const exchangeTotal = Number.parseFloat(raw.totals.exchange);
  const futuresTotal = Number.parseFloat(raw.totals.futures);

  const instant = processAssets(raw.instant, instantTotal);
  const exchange = processAssets(raw.exchange, exchangeTotal);
  const futures = processAssets(collapseFuturesByAsset(raw.futures), futuresTotal);
  // Legacy parity: the combined percentage denominator excludes futures
  const combined = mergeCombined(instant, exchange, instantTotal + exchangeTotal);

  return {
    combined,
    exchange,
    instant,
    futures,
    totals: {
      combined: instantTotal + exchangeTotal + futuresTotal,
      exchange: exchangeTotal,
      instant: instantTotal,
      futures: futuresTotal,
    },
  };
};

export const getPlatformBalances = () => bopApi.get(PATH).json<PlatformBalancesResponse>();
